import { useBookingsCountQuery } from '@typings/graphql'
import React from 'react'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Box
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { LocalizationProvider, DateRangePicker, DateRange } from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { deDE } from '@mui/x-date-pickers/locales'
import { de } from 'date-fns/locale'

import { StatisticCard } from './StatisticCard'

type TimelineOption = {
  label: string,
  range: DateRange<Date>
}

const TIMELINE_OPTIONS: TimelineOption[] = [
  {
    label: 'today',
    range: [
      new Date(new Date().setHours(0, 0, 0, 0)),
      new Date(new Date().setHours(23, 59, 59, 999))
    ]
  },
  {
    label: 'currentMonth',
    range: [new Date(new Date().getFullYear(), new Date().getMonth(), 1), new Date()]
  },
  {
    label: 'lastMonth',
    range: [new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1), new Date(new Date().getFullYear(), new Date().getMonth(), 0)]
  },
  {
    label: 'all',
    range: [new Date(1970, 0, 1), new Date()]
  }
]

export const TourBookingsList: React.FC = () => {
  const { t } = useTranslation()

  const { data, refetch } = useBookingsCountQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      data: {
        startDate: TIMELINE_OPTIONS[0].range[0],
        endDate: TIMELINE_OPTIONS[0].range[1]
      }
    }
  })

  const [selectedTimeline, setSelectedTimeline] = React.useState<string | null>(null)
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date> | null>(null)

  const mappedData = React.useMemo(() => {
    if (!data?.bookingsCount) {
      return []
    }

    return [...data.bookingsCount]?.sort((a, b) => b.count - a.count).map((tour) => {
      return {
        id: tour.id,
        value: `${tour.count} (${tour.percentage.toFixed(2)}%)`,
        label: tour.texts.title
      }
    })
  }, [data])

  const setAndFetchData = async (value?: DateRange<Date>) => {
    if (!value) {
      return
    }

    await refetch({
      data: {
        startDate: value[0],
        endDate: value[1]
      }
    })
  }

  const handleTimelineChange = async (value: string) => {
    setSelectedTimeline(value)
    setSelectedDateRange(null)

    await setAndFetchData(TIMELINE_OPTIONS.find((time) => time.label === value)?.range)
  }

  const handleDateRangeChange = async (value: DateRange<Date>) => {
    setSelectedTimeline(null)
    setSelectedDateRange(value)

    await setAndFetchData(value)
  }

  return (
    <StatisticCard>
      <Stack
        spacing={2}
      >
        <Box
          p={2}
          pb={0}
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <FormControl variant='standard' sx={{ width: '33%' }}>
            <InputLabel htmlFor="my-input">{t('payment.bookingTimeline')}</InputLabel>
            <Select onChange={(e) => handleTimelineChange(e.target.value)} value={selectedTimeline ?? ''}>
              {TIMELINE_OPTIONS.map((time) => (
                <MenuItem key={time.label} value={time.label}>
                  {t(`payment.timeline.${time.label}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div>
            <LocalizationProvider
              localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
              adapterLocale={de}
              dateAdapter={AdapterDateFns}
            >
              <DateRangePicker
                sx={{
                  width: '100%'
                }}
                value={selectedDateRange ?? [null, null]}
                onChange={(value) => handleDateRangeChange(value)}
              />
            </LocalizationProvider>
          </div>
        </Box>
        <TableContainer
          sx={{
            px: 2,
            pb: 2,
            maxHeight: 440,
            overflow: 'auto',
            '.MuiTableCell-root': {
              padding: '8px 0'
            }
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('payment.title')}</TableCell>
                <TableCell align="right">{t('payment.bookingCount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedData.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    ...(row.id === mappedData[mappedData.length - 1].id && {
                      'td, th': {
                        borderBottom: '1px solid #000'
                      }
                    })
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.label}
                  </TableCell>
                  <TableCell align="right">{row.value}</TableCell>
                </TableRow>
              ))}
              {mappedData.length > 0 && (
              <TableRow
                sx={{
                  'td, th': { border: 'none' }
                }}
              >
                <TableCell component="th" scope="row">{t('payment.overall')}</TableCell>
                <TableCell align="right">{mappedData.reduce((acc, row) => acc + Number(row.value.split(' ')[0]), 0)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </StatisticCard>
  )
}
